// The client's side in English or Dutch, as set per client in the studio.
// Only the interface is translated (labels, buttons, dates, money); what
// Tiago writes in a planning or estimate is shown as written.
import * as React from "react"

export type Lang = "en" | "nl"

const NL = {
    "Client access": "Klanttoegang",
    "your password": "je wachtwoord",
    "Not quite, try again?": "Klopt niet, nog een keer?",
    "Too many tries, wait a few minutes": "Te vaak geprobeerd, wacht een paar minuten",
    "Couldn't check, try again?": "Kon niet controleren, nog een keer?",
    "Local time": "Lokale tijd",
    Projects: "Projecten",
    project: "project",
    projects: "projecten",
    "All projects": "Alle projecten",
    Planning: "Planning",
    Estimate: "Offerte",
    Today: "Vandaag",
    Subtotal: "Subtotaal",
    VAT: "Btw",
    Total: "Totaal",
    "Valid until": "Geldig tot",
    "Approved on": "Goedgekeurd op",
    "Approve estimate": "Offerte goedkeuren",
    "incl. VAT": "incl. btw",
    "Approve {total}? This tells Tiago to go ahead.": "{total} goedkeuren? Dan kan Tiago aan de slag.",
    "Not sent, try again?": "Niet verstuurd, nog een keer?",
    "Not yet": "Nog niet",
    Yes: "Ja",
    "Approved on {date}. Thank you.": "Goedgekeurd op {date}. Dank je wel.",
    "Export as image": "Exporteren als afbeelding",
    "Export the planning as an image": "De planning exporteren als afbeelding",
    "Export the estimate as an image": "De offerte exporteren als afbeelding",
    "Saving…": "Opslaan…",
    "Estimate to approve": "Offerte goed te keuren",
    "Starts {date}": "Begint {date}",
    Finished: "Afgerond",
    "{phase}, in progress": "{phase}, bezig",
    "In progress": "Bezig",
    "Log out": "Uitloggen",
    "There's no project here. Open the menu to see yours.": "Hier staat geen project. Open het menu voor je projecten.",
    "Open the link you received to see your projects.": "Open de link die je hebt gekregen om je projecten te zien.",
    "Something went wrong. Try again in a moment.": "Er ging iets mis. Probeer het zo nog eens.",
    days: "dagen",
    hours: "uur",
    pieces: "stuks",
    fixed: "vast",
} as const

type Key = keyof typeof NL

let current: Lang = "en"
try {
    // (remembered, so the password screen is already in the right language on a return visit)
    const saved = localStorage.getItem("nto-lang")
    if (saved === "nl" || saved === "en") current = saved
} catch {}

export const lang = () => current
let listeners: (() => void)[] = []
/** remember: false for the studio, which is always in English */
export function setLang(l: Lang | null | undefined, remember = true) {
    const next: Lang = l === "nl" ? "nl" : "en"
    const changed = next !== current
    current = next
    document.documentElement.lang = current
    if (remember)
        try {
            localStorage.setItem("nto-lang", current)
        } catch {}
    if (changed) for (const f of listeners) f()
}
/** re-renders when the language changes */
export function useLang() {
    const [, bump] = React.useReducer((x: number) => x + 1, 0)
    React.useEffect(() => {
        listeners.push(bump)
        return () => void (listeners = listeners.filter((f) => f !== bump))
    }, [])
    return current
}
// (an exported image takes its client's language, wherever it's made)
let override: Lang | null = null
export function inLang<T>(l: Lang | undefined, f: () => Promise<T>): Promise<T> {
    override = l || null
    return f().finally(() => (override = null))
}
const now = () => override || current
export const locale = () => (now() === "nl" ? "nl-NL" : "en-GB")

/** a text in the client's language; {name} parts filled in */
export function t(key: Key, vars: Record<string, string | number> = {}) {
    let s: string = now() === "nl" ? NL[key] : key
    for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v))
    return s
}

/** a unit as shown (days, hours, pieces, fixed) */
export const unit = (u: string) => (u in NL ? t(u as Key) : u)
